import {
  AbsolutePositionAligner,
  Button,
  MenuItem,
  useDocumentEvent,
} from '@sgcmpteltd/uikit-react';
import {
  DOCUMENT_EVENT_NAME_CLICK,
} from '@sgcmpteltd/uikit-react/hooks/use-document-event';
import {
  alignBottomXBegin,
} from '@sgcmpteltd/uikit-react/utils/position/absolute';
import cx from 'clsx';
import type { MouseEventHandler } from 'react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './styles.module.scss';
import type { Component, SelectType } from './types';

export const DualSelect: Component = ({
  buttonText,
  name,
  lowerOptionList,
  upperOptionList,
  lowerValue,
  upperValue,
  rectCalculator = alignBottomXBegin,
  onChange,
  onClear,
  onApply,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [seletType, setSelectType] = useState<SelectType>('lower');
  const buttonRootElRef = useRef<HTMLDivElement>(null);
  const menuRootElRef = useRef<HTMLDivElement>(null);
  const buttonApplyElRef = useRef<HTMLDivElement>(null);
  const onDocMouseClickRef = useRef<Document['onclick'] | null>(null);

  const handleButtonClick = () => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      return;
    }
    setIsDropdownOpen(true);
  };

  const handleLowerButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setSelectType('lower');

    if (seletType === 'lower' && isMenuOpen) {
      setIsMenuOpen(false);
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleUpperButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setSelectType('upper');

    if (seletType === 'upper' && isMenuOpen) {
      setIsMenuOpen(false);
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleMenuItemClick = (
    selectType: SelectType,
    text: string,
  ) => (value?: string | number) => {
    setIsMenuOpen(false);

    if (value !== undefined) {
      if (selectType === 'lower') {
        onChange?.({
          name,
          type: 'range',
          lowerValue: {
            text,
            value,
          },
          upperValue,
        });
      } else {
        onChange?.({
          name,
          type: 'range',
          lowerValue,
          upperValue: {
            text,
            value,
          },
        });
      }
    }
  };

  const handleClearClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    onClear?.();
    setIsDropdownOpen(false);
  };

  const handleApplyClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    onApply?.({
      name,
      type: 'range',
      lowerValue,
      upperValue,
    });
    setIsDropdownOpen(false);
    setHasApplied(true);
  };

  useEffect(() => {
    if (hasApplied) {
      setIsDropdownOpen(false);
      setHasApplied(false);
    }
  }, [hasApplied]);

  const lowerSelectOptionList = useMemo(() => {
    const index = upperOptionList.findIndex(
      opt => opt.value === upperValue.value,
    );

    if (index === -1) return lowerOptionList;

    return lowerOptionList.map((option, idx) => {
      if (idx > index) {
        return { ...option, isDisabled: true };
      }
      return option;
    });
  }, [lowerOptionList, upperOptionList, upperValue]);

  const upperSelectOptionList = useMemo(() => {
    const index = lowerOptionList.findIndex(
      option => option.value === lowerValue.value,
    );

    if (index === -1) return upperOptionList;

    return upperOptionList.map((option, idx) => {
      if (idx < index) {
        return { ...option, isDisabled: true };
      }
      return option;
    });
  }, [lowerOptionList, lowerValue, upperOptionList]);

  onDocMouseClickRef.current = ({ target }) => {
    if (
      (target instanceof Node
      && (buttonRootElRef.current?.contains(target)
      || menuRootElRef.current?.contains(target)
      || buttonApplyElRef.current?.contains(target)
      ))
    ) {
      return;
    }
    setIsDropdownOpen(false);
  };

  useDocumentEvent({
    eventName: DOCUMENT_EVENT_NAME_CLICK,
    eventHandlerRef: onDocMouseClickRef,
  });

  const targetElement = (
    <div
      ref={menuRootElRef}
      className={styles.menuRoot}>
      <div>
        <div className={styles.containerSummary}>
          <span className={styles.textName}>
            {name}
          </span>
          <div className={styles.selectContainer}>
            <button
              className={cx(
                styles.select,
                isMenuOpen
                && seletType === 'lower'
                && styles.isSelectOpen,
              )}
              tabIndex={0}
              onClick={handleLowerButtonClick}>
              <div className={styles.textValue}>
                {lowerValue.text}
              </div>
            </button>
            <span>to</span>
            <button
              className={cx(
                styles.select,
                isMenuOpen
                && seletType === 'upper'
                && styles.isSelectOpen,
              )}
              tabIndex={0}
              onClick={handleUpperButtonClick}>
              <div className={styles.textValue}>
                {upperValue.text}
              </div>
            </button>
          </div>
        </div>
        <menu className={cx(
          styles.menuContainer,
          {
            [styles.open]: isMenuOpen,
          },
        )}>
          {seletType === 'lower'
            ? lowerSelectOptionList.map(opt => (
              <MenuItem
                key={`lower-${opt.text}`}
                isActive={opt.value === lowerValue.value}
                isDisabled={opt.isDisabled}
                value={opt.value}
                onClick={handleMenuItemClick(seletType, opt.text)}>
                {opt.text}
              </MenuItem>
            ))
            : upperSelectOptionList.toReversed().map(opt => (
              <MenuItem
                key={`upper-${opt.text}$`}
                isActive={opt.value === upperValue.value}
                isDisabled={opt.isDisabled}
                value={opt.value}
                onClick={handleMenuItemClick(seletType, opt.text)}>
                {opt.text}
              </MenuItem>
            ))}
        </menu>
      </div>
      <div
        ref={buttonApplyElRef}
        className={styles.buttonApply}>
        <button
          className={styles.textClear}
          onClick={handleClearClick}>
          Clear
        </button>
        <Button
          className={cx(styles.searchButton, styles.isRound)}
          variant="contained"
          onClick={handleApplyClick}>
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <AbsolutePositionAligner
      isActive={isDropdownOpen}
      rectCalculator={rectCalculator}
      targetElement={targetElement}>
      <div
        ref={buttonRootElRef}
        role="button"
        tabIndex={0}
        onClick={handleButtonClick}
        onKeyDown={handleButtonClick}>
        <div className={`${cx(styles.containerHead, isDropdownOpen && styles.isDropdownOpen)}
        ${buttonText !== 'Price Range' && buttonText !== 'Depreciation' ? styles.activeContainerHead : ''}`}>
          <div className={styles.textButton}>
            {buttonText}
          </div>
        </div>
      </div>
    </AbsolutePositionAligner>
  );
};
